
import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Box,
  IconButton,
  Stepper,
  StepLabel,
  Step,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import NavBar from "components/navbar";
import { LandingFooter } from "./LandingFooter";
import { useNavigate } from "react-router-dom";
import { useLazyGetShipmentStatusQuery } from "store/services/publicApi";
import { useUrlParams } from "hooks/useUrlParams";

const shipmentSteps = ["Pending", "Collected", "Loaded", "Delivered"];

const statusToStepMapping = {
  Pending: 0,
  Collected: 1,
  Consolidation: 1,
  Loaded: 2,
  Unloaded: 2,
  Deconsolidation: 2,
  CrossDocked: 2,
  SendOut: 2,
  Warehousing: 2,
  Delivered: 3,
  DeliveryFailed: 2,
  Return: 2,
  Cancelled: 0,
};

const getCurrentStepIndex = (status) => {
  if (!status) return 0;
  const stepIndex = statusToStepMapping[status] ?? 0;
  return stepIndex;
};

const TrackingShipment = () => {

  const [input, setInput] = useState("");

  const { params, appendParams } = useUrlParams()

  const shipmentId = params.shipmentId;

  const reference = params.reference;

  const [trackedItems, setTrackedItems] = useState([])

  const [shipmentData, setShipmentData] = useState([])

  const [
    fetchByQuery,
    { isLoading: queryLoading, isFetching: queryFetching, error: queryError },
  ] = useLazyGetShipmentStatusQuery();


  const isLoading = queryLoading;
  const isFetching = queryFetching;
  const error = queryError;

  // Fetch data when URL params change
  useEffect(() => {
    if (shipmentId || reference) {
      const params = {};
      if (shipmentId) params.shipmentId = shipmentId;
      if (reference) params.reference = reference;

      fetchByQuery(params).then((res) => {
        if (res?.data?.length) {
          const data = res.data;
          const newIds = data.flatMap((item) => [item.shipmentId, item.reference]).filter(Boolean);
          setShipmentData(data);
          setTrackedItems((prev) => Array.from(new Set([...prev, ...newIds])));
        } else {
          toast("No shipment found for the entered ID or reference.", "error");
        }
      });
    }
  }, [shipmentId, reference, fetchByQuery]);



  const handleAddTrackedItem = async () => {
    const searchValues = input
      .trim()
      .replace(/-/g, "")
      .split(/[\s,]+/)
      .filter((value) => value);

    if (!searchValues.length) {
      toast("Please enter at least one shipment ID or reference.");
      return;
    }

    const newItems = searchValues.filter((value) => !trackedItems.includes(value));
    // Try both query and ID endpoints for each new item
    appendParams({ shipmentId: [...trackedItems, ...newItems] })
    setInput("");
  };

  // handle to clear all
  const handleClearAll = () => {
    setTrackedItems([]);
    setShipmentData([]);

    const url = new URL(window.location);
    url.searchParams.delete("shipmentId");
    url.searchParams.delete("reference");

    window.history.replaceState({}, "", url.pathname + url.search);
  };


  const handleDeleteItem = (searchValue) => {
    const updatedTrackedItems = trackedItems.filter((item) => item !== searchValue);
    setTrackedItems(updatedTrackedItems);

    setShipmentData((prev) =>
      prev.filter(
        (item) => item.shipmentId !== searchValue && item.reference !== searchValue
      )
    );
    const url = new URL(window.location);
    const currentIds = url.searchParams.get("shipmentId")?.split(",") || [];

    const filteredIds = currentIds
      .map((id) => id.trim())
      .filter((id) => id !== searchValue);
    if (filteredIds.length > 0) {
      url.searchParams.set("shipmentId", filteredIds.join(","));
    } else {
      url.searchParams.delete("shipmentId");
    }
    window.history.replaceState({}, "", url.pathname + url.search);
  };



  return (
    <>
      <NavBar />
      <Box sx={{ minHeight: "61vh", bgcolor: "background.default", mt: 10 }}>
        <Box sx={{ minWidth: { xs: "95%", lg: 1100 }, mx: "auto", mt: 1, mb: 4, maxWidth: { lg: "60%" } }}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h5" fontWeight={600} mb={2}>
                Track Multiple Shipments
              </Typography>
              <Box display="flex" sx={{ flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
                <TextField
                  fullWidth
                  label="Enter Shipment IDs or References (separated by commas or spaces)"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                />
                <Button
                  variant="contained"
                  onClick={handleAddTrackedItem}
                  disabled={!input.trim()}
                  sx={{
                    bgcolor: "crimson",
                    "&:hover": { bgcolor: "crimson" },
                    width: "110px",
                  }}
                >
                  Track
                </Button>
                <Button
                  variant="contained"
                  onClick={handleClearAll}
                  sx={{
                    bgcolor: "crimson",
                    "&:hover": { bgcolor: "crimson" },
                    width: "150px",
                  }}
                >
                  Clear All
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ maxWidth: 1100, mx: "auto" }}>
          {isLoading || isFetching ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Typography color="error" textAlign="center">
              Error loading shipment data
            </Typography>
          ) : (
            shipmentData?.map((item) => (
              <ShipmentStatusCard
                key={item.shipmentId || item.reference}
                searchValue={trackedItems.find((id) => id === item.shipmentId || id === item.reference) || item.shipmentId}
                data={item}
                onDelete={handleDeleteItem}
              />
            ))
          )}
        </Box>
      </Box>
      <LandingFooter />
    </>
  );
};


const ShipmentStatusCard = ({ searchValue, onDelete, data }) => {

  const navigate = useNavigate();

  const currentStepIndex = getCurrentStepIndex(data?.status);

  if (!data) return null;

  return (
    <Card elevation={3} sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" gutterBottom color="primary.main">
            {data.shipmentId ? `Shipment ID: ${data.shipmentId}` : ""}
            {data.shipmentId && data.reference ? " | " : ""}
            {data.reference ? `Reference: ${data.reference}` : ""}
          </Typography>
          {onDelete && (
            <IconButton
              onClick={() => onDelete(searchValue)}
              aria-label={`Delete ${searchValue}`}
              color="error"
            >
              <CloseIcon />
            </IconButton>
          )}

        </Box>
        <Stepper activeStep={currentStepIndex} alternativeLabel>
          {shipmentSteps.map((label) => {
            const stepIndex = shipmentSteps.indexOf(label);
            const isCompleted = currentStepIndex >= stepIndex;
            const isActive = currentStepIndex === stepIndex;

            return (
              <Step key={label} completed={isCompleted}>
                <StepLabel
                  sx={{
                    ...(isActive || isCompleted ? {
                      color: "success.main",
                      ".MuiStepIcon-root": { color: "primary.main" }
                    } : {})
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>
        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button
            variant="contained"
            onClick={() => navigate(`/tracking/status/${data.shipmentId}`)}
            sx={{
              bgcolor: "crimson",
              color: "white",
              "&:hover": { bgcolor: "darkred" },
              height: "40px",
            }}
          >
            View Details
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default TrackingShipment;